import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { getAuth, updatePassword, reauthenticateWithCredential, EmailAuthProvider, deleteUser } from "firebase/auth";
import axios from "axios";
import OfertaCard from "./OfertaCard";

// ============================================================
// Sub-componente: Aba de Privacidade & Dados (LGPD Art. 18)
// ============================================================
const PrivacidadeDados = ({ user, getFreshToken, logout }) => {
    const [showData, setShowData] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const [status, setStatus] = useState({ type: '', msg: '' });

    const handleExportData = async () => {
        try {
            const token = await getFreshToken();
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/users/${user.uid}/export`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `ofertiz_meus_dados_${new Date().toISOString().slice(0, 10)}.json`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Erro ao exportar dados:', error);
            setStatus({ type: 'error', msg: 'Erro ao exportar dados. Tente novamente.' });
        }
    };

    const handleDeleteAccount = async () => {
        if (confirmText !== 'EXCLUIR') {
            setStatus({ type: 'error', msg: 'Digite EXCLUIR para confirmar.' });
            return;
        }
        setDeleting(true);
        try {
            const token = await getFreshToken();
            // Excluir dados do backend
            await axios.delete(`${process.env.REACT_APP_API_URL}/users/${user.uid}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            // Excluir conta do Firebase Auth
            const auth = getAuth();
            await deleteUser(auth.currentUser);
            logout();
        } catch (error) {
            console.error('Erro ao excluir conta:', error);
            setStatus({ type: 'error', msg: 'Erro ao excluir conta. Pode ser necessário fazer login novamente.' });
            setDeleting(false);
        }
    };

    const direitos = [
        { icon: 'fa-eye', title: 'Acesso', desc: 'Ver seus dados armazenados' },
        { icon: 'fa-download', title: 'Portabilidade', desc: 'Baixar seus dados em JSON' },
        { icon: 'fa-trash-alt', title: 'Eliminação', desc: 'Excluir sua conta e dados' },
    ];

    return (
        <div>
            <h2 style={{ marginBottom: '20px', borderBottom: '1px solid var(--border-color)', paddingBottom: '10px' }}>
                Privacidade & Dados
            </h2>
            <div style={{ marginBottom: '20px', padding: '14px', backgroundColor: 'rgba(52,131,250,0.06)', borderRadius: '8px', border: '1px solid rgba(52,131,250,0.15)', fontSize: '14px', color: '#1a56c4' }}>
                <i className="fas fa-shield-alt" style={{ marginRight: '8px' }}></i>
                Conforme a <strong>LGPD (Art. 18)</strong>, você tem direito de acessar, corrigir, exportar e excluir seus dados pessoais.
            </div>

            {status.msg && (
                <div style={{ padding: '10px', marginBottom: '15px', borderRadius: '4px', backgroundColor: status.type === 'error' ? '#ffebee' : '#e8f5e9', color: status.type === 'error' ? '#c62828' : '#2e7d32' }}>
                    {status.msg}
                </div>
            )}

            {/* Cards de Direitos */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px', marginBottom: '30px' }}>
                {direitos.map(({ icon, title, desc }) => (
                    <div key={title} style={{ padding: '16px', border: '1px solid var(--border-color)', borderRadius: '8px', textAlign: 'center' }}>
                        <i className={`fas ${icon}`} style={{ fontSize: '22px', color: 'var(--primary-color)', marginBottom: '8px' }}></i>
                        <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '4px' }}>{title}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{desc}</div>
                    </div>
                ))}
            </div>

            {/* Ver Meus Dados */}
            <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '15px', fontWeight: '700', marginBottom: '10px' }}>
                    <i className="fas fa-eye" style={{ marginRight: '8px', color: 'var(--primary-color)' }}></i>
                    Dados Armazenados
                </h3>
                <button
                    onClick={() => setShowData(v => !v)}
                    style={{ padding: '8px 16px', border: '1px solid var(--border-color)', borderRadius: '6px', cursor: 'pointer', background: 'white', fontSize: '13px', fontWeight: '600', color: 'var(--primary-color)' }}
                >
                    {showData ? 'Ocultar' : 'Ver meus dados'}
                </button>
                {showData && (
                    <div style={{ marginTop: '14px', fontSize: '14px', backgroundColor: '#f9f9f9', padding: '14px', borderRadius: '6px', lineHeight: '1.8' }}>
                        <div><strong>Nome:</strong> {user.name || '—'}</div>
                        <div><strong>E-mail:</strong> {user.email}</div>
                        <div><strong>ID:</strong> <code style={{ fontSize: '12px' }}>{user.uid}</code></div>
                        <div><strong>Foto de perfil:</strong> {user.photoURL ? 'Sim' : 'Não'}</div>
                        <div><strong>Provedor:</strong> {user.providerData?.[0]?.providerId || 'email'}</div>
                    </div>
                )}
            </div>

            {/* Exportar Dados */}
            <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '15px', fontWeight: '700', marginBottom: '6px' }}>
                    <i className="fas fa-download" style={{ marginRight: '8px', color: 'var(--primary-color)' }}></i>
                    Exportar Meus Dados
                </h3>
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                    Baixe uma cópia de todos os dados que armazenamos sobre você em formato JSON.
                </p>
                <button
                    onClick={handleExportData}
                    style={{ padding: '8px 16px', backgroundColor: 'var(--primary-color)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}
                >
                    <i className="fas fa-file-download" style={{ marginRight: '6px' }}></i>
                    Baixar meus dados (JSON)
                </button>
            </div>

            {/* Excluir Conta */}
            <div style={{ border: '1px solid #ffcdd2', borderRadius: '8px', padding: '20px', backgroundColor: '#fff9f9' }}>
                <h3 style={{ fontSize: '15px', fontWeight: '700', marginBottom: '6px', color: '#c62828' }}>
                    <i className="fas fa-trash-alt" style={{ marginRight: '8px' }}></i>
                    Excluir Minha Conta
                </h3>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                    Esta ação é <strong>permanente e irreversível</strong>. Todos os seus dados, comentários, favoritos e pontos serão excluídos em até 30 dias.
                </p>
                <p style={{ fontSize: '13px', color: '#c62828', marginBottom: '8px', fontWeight: '600' }}>
                    Para confirmar, digite <strong>EXCLUIR</strong> no campo abaixo:
                </p>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <input
                        type="text"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder="Digite: EXCLUIR"
                        style={{ padding: '8px 12px', border: '1px solid #ffcdd2', borderRadius: '6px', fontSize: '13px', backgroundColor: 'white', minWidth: '180px' }}
                    />
                    <button
                        onClick={handleDeleteAccount}
                        disabled={deleting}
                        style={{ padding: '8px 16px', backgroundColor: deleting ? '#aaa' : '#d32f2f', color: 'white', border: 'none', borderRadius: '6px', cursor: deleting ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '600' }}
                    >
                        {deleting ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: '6px' }}></i>Excluindo...</> : 'Excluir minha conta'}
                    </button>
                </div>
                <p style={{ fontSize: '12px', color: '#999', marginTop: '12px' }}>
                    Dúvidas? Entre em contato pelo{' '}
                    <a href="mailto:contato@ofertiz.com.br?subject=LGPD — Solicitar Exclusão de Conta" style={{ color: 'var(--primary-color)' }}>canal de privacidade</a>
                    {' '}antes de excluir.
                </p>
            </div>

            <div style={{ marginTop: '20px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <Link to="/politica-de-privacidade" style={{ color: 'var(--primary-color)', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    <i className="fas fa-shield-alt"></i> Política de Privacidade
                </Link>
                <Link to="/termos-de-uso" style={{ color: 'var(--text-secondary)', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    <i className="fas fa-file-contract"></i> Termos de Uso
                </Link>
            </div>
        </div>
    );
};


const UserProfile = ({ favoritesList, toggleFavorite }) => {
    const { user, updateProfile, logout, getFreshToken } = useAuth();
    const location = useLocation();
    const [activeTab, setActiveTab] = useState(location.state?.activeTab || "favoritos");
    const [formData, setFormData] = useState({
        telefone: "",
        cpf: "",
        chave_pix: "",
        cep: "",
        rua: "",
        numero: "",
        complemento: "",
        bairro: "",
        cidade: "",
        estado: "",
        ponto_referencia: ""
    });
    const [passwordData, setPasswordData] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
    });
    const [status, setStatus] = useState({ type: "", msg: "" });
    const [passwordStatus, setPasswordStatus] = useState({ type: "", msg: "" });
    const [uploadingPhoto, setUploadingPhoto] = useState(false);

    useEffect(() => {
        if (user) {
            setFormData({
                telefone: user.telefone || "",
                cpf: user.cpf || "",
                chave_pix: user.chave_pix || "",
                cep: user.cep || "",
                rua: user.rua || "",
                numero: user.numero || "",
                complemento: user.complemento || "",
                bairro: user.bairro || "",
                cidade: user.cidade || "",
                estado: user.estado || "",
                ponto_referencia: user.ponto_referencia || ""
            });
        }
    }, [user]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handlePasswordChangeInput = (e) => {
        setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
    };

    const handleCepBlur = async (e) => {
        const cep = e.target.value.replace(/\D/g, '');
        if (cep.length === 8) {
            try {
                const response = await axios.get(`https://viacep.com.br/ws/${cep}/json/`);
                if (!response.data.erro) {
                    setFormData(prev => ({
                        ...prev,
                        rua: response.data.logradouro,
                        bairro: response.data.bairro,
                        cidade: response.data.localidade,
                        estado: response.data.uf
                    }));
                }
            } catch (error) {
                console.error("Erro ao buscar CEP:", error);
            }
        }
    };

    const handlePhotoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploadingPhoto(true);
        const formDataUpload = new FormData();
        formDataUpload.append('image', file);

        try {
            // Upload para o backend (reusando endpoint de banner que aceita imagens)
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/upload/banner`, formDataUpload, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            const photoURL = response.data.imageUrl;

            // Atualizar perfil com nova foto
            await updateProfile({ ...formData, photoURL });
            setStatus({ type: "success", msg: "Foto atualizada com sucesso!" });
        } catch (error) {
            console.error("Erro ao fazer upload da foto:", error);
            setStatus({ type: "error", msg: "Erro ao atualizar foto." });
        } finally {
            setUploadingPhoto(false);
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setPasswordStatus({ type: "error", msg: "As senhas não coincidem." });
            return;
        }

        if (passwordData.newPassword.length < 6) {
            setPasswordStatus({ type: "error", msg: "A senha deve ter pelo menos 6 caracteres." });
            return;
        }

        setPasswordStatus({ type: "info", msg: "Atualizando senha..." });

        try {
            const auth = getAuth();
            const user = auth.currentUser;

            // Re-autenticar usuário
            const credential = EmailAuthProvider.credential(user.email, passwordData.currentPassword);
            await reauthenticateWithCredential(user, credential);

            // Atualizar senha
            await updatePassword(user, passwordData.newPassword);

            setPasswordStatus({ type: "success", msg: "Senha atualizada com sucesso!" });
            setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
        } catch (error) {
            console.error("Erro ao atualizar senha:", error);
            if (error.code === 'auth/wrong-password') {
                setPasswordStatus({ type: "error", msg: "Senha atual incorreta." });
            } else {
                setPasswordStatus({ type: "error", msg: "Erro ao atualizar senha. Tente novamente." });
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus({ type: "info", msg: "Salvando..." });
        try {
            await updateProfile(formData);

            // Tentar reivindicar recompensa de perfil completo
            try {
                const token = await getFreshToken();
                if (token) {
                    const rewardResponse = await axios.post(
                        `${process.env.REACT_APP_API_URL}/points/profile`,
                        {},
                        { headers: { Authorization: `Bearer ${token}` } }
                    );

                    if (rewardResponse.data.rewarded) {
                        setStatus({ type: "success", msg: `Dados atualizados! ${rewardResponse.data.message}` });
                    } else {
                        setStatus({ type: "success", msg: "Dados atualizados com sucesso!" });
                    }
                }
            } catch (rewardError) {
                console.error("Erro ao processar recompensa de perfil:", rewardError);
                setStatus({ type: "success", msg: "Dados atualizados com sucesso!" });
            }

        } catch (error) {
            console.error(error);
            setStatus({ type: "error", msg: "Erro ao atualizar dados." });
        }
    };

    if (!user) {
        return (
            <div className="container" style={{ padding: "50px 0", textAlign: "center" }}>
                <h2>Acesso Negado</h2>
                <p>Você precisa estar logado para acessar esta página.</p>
                <Link to="/login" style={{ color: "var(--primary-color)", fontWeight: "bold" }}>
                    Ir para Login
                </Link>
            </div>
        );
    }

    const menuItems = [
        { id: "dados", label: "Meus Dados", icon: "fas fa-user" },
        { id: "carteira", label: "Minha Carteira", icon: "fas fa-wallet" },
        { id: "seguranca", label: "Segurança", icon: "fas fa-lock" },
        { id: "pedidos", label: "Meus Pedidos", icon: "fas fa-box" },
        { id: "favoritos", label: "Meus Favoritos", icon: "fas fa-heart" },
        { id: "privacidade", label: "Privacidade & Dados", icon: "fas fa-shield-alt" },
    ];

    return (
        <div className="container" style={{ padding: "var(--spacing-lg) var(--spacing-md)" }}>
            <h1 style={{ marginBottom: "var(--spacing-lg)", fontSize: "24px" }}>Minha Conta</h1>

            <div className="grid grid-cols-1 grid-md-4" style={{ gap: "var(--spacing-lg)", gridTemplateColumns: "250px 1fr" }}>
                {/* Sidebar Menu */}
                <div style={{
                    backgroundColor: "var(--white)",
                    borderRadius: "var(--radius-md)",
                    boxShadow: "var(--shadow-sm)",
                    overflow: "hidden",
                    height: "fit-content"
                }}>
                    <div style={{ padding: "20px", borderBottom: "1px solid var(--border-color)", textAlign: "center" }}>
                        <div style={{
                            width: "60px",
                            height: "60px",
                            backgroundColor: "var(--primary-color)",
                            color: "white",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "24px",
                            margin: "0 auto 10px",
                            overflow: "hidden",
                            position: "relative"
                        }}>
                            {user.photoURL ? (
                                <img src={user.photoURL} alt={user.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            ) : (
                                user.email.charAt(0).toUpperCase()
                            )}
                            <label htmlFor="photo-upload" style={{
                                position: "absolute",
                                bottom: "0",
                                right: "0",
                                left: "0",
                                top: "0",
                                backgroundColor: "rgba(0,0,0,0.5)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                opacity: "0",
                                transition: "opacity 0.2s",
                                cursor: "pointer",
                                color: "white"
                            }}
                                onMouseEnter={(e) => e.currentTarget.style.opacity = "1"}
                                onMouseLeave={(e) => e.currentTarget.style.opacity = "0"}
                            >
                                <i className="fas fa-camera"></i>
                            </label>
                            <input
                                id="photo-upload"
                                type="file"
                                accept="image/*"
                                onChange={handlePhotoUpload}
                                style={{ display: "none" }}
                                disabled={uploadingPhoto}
                            />
                        </div>
                        <div style={{ fontWeight: "bold", wordBreak: "break-all" }}>{user.name || user.email.split("@")[0]}</div>
                        <div style={{ fontSize: "12px", color: "var(--text-secondary)" }}>{user.email}</div>
                    </div>

                    <nav>
                        {menuItems.map((item) => (
                            item.id === 'carteira' ? (
                                <Link
                                    key={item.id}
                                    to="/carteira"
                                    style={{
                                        width: "100%",
                                        textAlign: "left",
                                        padding: "15px 20px",
                                        border: "none",
                                        backgroundColor: "transparent",
                                        color: "var(--text-primary)",
                                        textDecoration: "none",
                                        fontWeight: "normal",
                                        borderLeft: "4px solid transparent",
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "10px",
                                        fontSize: "14px"
                                    }}
                                >
                                    <i className={item.icon} style={{ width: "20px" }}></i> {item.label}
                                </Link>
                            ) : (
                                <button
                                    key={item.id}
                                    onClick={() => setActiveTab(item.id)}
                                    style={{
                                        width: "100%",
                                        textAlign: "left",
                                        padding: "15px 20px",
                                        border: "none",
                                        backgroundColor: activeTab === item.id ? "rgba(52, 131, 250, 0.1)" : "transparent",
                                        color: activeTab === item.id ? "var(--primary-color)" : "var(--text-primary)",
                                        fontWeight: activeTab === item.id ? "bold" : "normal",
                                        borderLeft: activeTab === item.id ? "4px solid var(--primary-color)" : "4px solid transparent",
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "10px",
                                        fontSize: "14px"
                                    }}
                                >
                                    <i className={item.icon} style={{ width: "20px" }}></i> {item.label}
                                </button>
                            )
                        ))}
                        <button
                            onClick={logout}
                            style={{
                                width: "100%",
                                textAlign: "left",
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                padding: "15px 20px",
                                color: "#d32f2f",
                                background: "none",
                                border: "none",
                                fontSize: "14px",
                                borderTop: "1px solid var(--border-color)",
                                cursor: "pointer"
                            }}
                        >
                            <i className="fas fa-sign-out-alt" style={{ width: "20px" }}></i> Sair
                        </button>
                    </nav>
                </div>

                {/* Content Area */}
                <div style={{ backgroundColor: "var(--white)", borderRadius: "var(--radius-md)", boxShadow: "var(--shadow-sm)", padding: "var(--spacing-lg)", minHeight: "400px" }}>

                    {activeTab === "dados" && (
                        <div>
                            <h2 style={{ marginBottom: "20px", borderBottom: "1px solid var(--border-color)", paddingBottom: "10px" }}>Meus Dados</h2>

                            {status.msg && (
                                <div style={{
                                    padding: "10px",
                                    marginBottom: "15px",
                                    borderRadius: "4px",
                                    backgroundColor: status.type === "success" ? "#e8f5e9" : status.type === "error" ? "#ffebee" : "#e3f2fd",
                                    color: status.type === "success" ? "#2e7d32" : status.type === "error" ? "#c62828" : "#1565c0"
                                }}>
                                    {status.msg}
                                </div>
                            )}

                            <form onSubmit={handleSubmit} style={{ maxWidth: "500px" }}>
                                <div style={{ marginBottom: "15px" }}>
                                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Nome Completo</label>
                                    <input type="text" value={user.name || ""} disabled style={{ width: "100%", padding: "10px", borderRadius: "4px", border: "1px solid #ddd", backgroundColor: "#f9f9f9" }} />
                                </div>
                                <div style={{ marginBottom: "15px" }}>
                                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Email</label>
                                    <input type="email" value={user.email} disabled style={{ width: "100%", padding: "10px", borderRadius: "4px", border: "1px solid #ddd", backgroundColor: "#f9f9f9" }} />
                                </div>
                                <div style={{ marginBottom: "15px" }}>
                                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Telefone (WhatsApp)</label>
                                    <input
                                        type="text"
                                        name="telefone"
                                        value={formData.telefone}
                                        onChange={handleChange}
                                        placeholder="(11) 99999-9999"
                                        style={{ width: "100%", padding: "10px", borderRadius: "4px", border: "1px solid #ddd" }}
                                    />
                                </div>
                                <div style={{ marginBottom: "15px" }}>
                                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>CPF (Opcional)</label>
                                    <input
                                        type="text"
                                        name="cpf"
                                        value={formData.cpf}
                                        onChange={handleChange}
                                        placeholder="000.000.000-00"
                                        style={{ width: "100%", padding: "10px", borderRadius: "4px", border: "1px solid #ddd" }}
                                    />
                                </div>
                                <div style={{ marginBottom: "15px" }}>
                                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Chave PIX (Para Cashback)</label>
                                    <input
                                        type="text"
                                        name="chave_pix"
                                        value={formData.chave_pix}
                                        onChange={handleChange}
                                        placeholder="Email, CPF ou Telefone"
                                        style={{ width: "100%", padding: "10px", borderRadius: "4px", border: "1px solid #ddd" }}
                                    />
                                </div>

                                <h3 style={{ marginTop: "30px", marginBottom: "15px", borderBottom: "1px solid #eee", paddingBottom: "10px" }}>Endereço</h3>

                                <div className="grid grid-cols-1 grid-md-2" style={{ gap: "15px" }}>
                                    <div style={{ marginBottom: "15px" }}>
                                        <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>CEP</label>
                                        <input
                                            type="text"
                                            name="cep"
                                            value={formData.cep}
                                            onChange={handleChange}
                                            onBlur={handleCepBlur}
                                            placeholder="00000-000"
                                            maxLength="9"
                                            style={{ width: "100%", padding: "10px", borderRadius: "4px", border: "1px solid #ddd" }}
                                        />
                                    </div>
                                    <div style={{ marginBottom: "15px" }}>
                                        <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Estado</label>
                                        <input
                                            type="text"
                                            name="estado"
                                            value={formData.estado}
                                            onChange={handleChange}
                                            style={{ width: "100%", padding: "10px", borderRadius: "4px", border: "1px solid #ddd", backgroundColor: "#f9f9f9" }}
                                            readOnly
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 grid-md-2" style={{ gap: "15px" }}>
                                    <div style={{ marginBottom: "15px" }}>
                                        <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Cidade</label>
                                        <input
                                            type="text"
                                            name="cidade"
                                            value={formData.cidade}
                                            onChange={handleChange}
                                            style={{ width: "100%", padding: "10px", borderRadius: "4px", border: "1px solid #ddd", backgroundColor: "#f9f9f9" }}
                                            readOnly
                                        />
                                    </div>
                                    <div style={{ marginBottom: "15px" }}>
                                        <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Bairro</label>
                                        <input
                                            type="text"
                                            name="bairro"
                                            value={formData.bairro}
                                            onChange={handleChange}
                                            style={{ width: "100%", padding: "10px", borderRadius: "4px", border: "1px solid #ddd" }}
                                        />
                                    </div>
                                </div>

                                <div style={{ marginBottom: "15px" }}>
                                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Rua / Logradouro</label>
                                    <input
                                        type="text"
                                        name="rua"
                                        value={formData.rua}
                                        onChange={handleChange}
                                        style={{ width: "100%", padding: "10px", borderRadius: "4px", border: "1px solid #ddd" }}
                                    />
                                </div>

                                <div className="grid grid-cols-1 grid-md-2" style={{ gap: "15px" }}>
                                    <div style={{ marginBottom: "15px" }}>
                                        <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Número</label>
                                        <input
                                            type="text"
                                            name="numero"
                                            value={formData.numero}
                                            onChange={handleChange}
                                            style={{ width: "100%", padding: "10px", borderRadius: "4px", border: "1px solid #ddd" }}
                                        />
                                    </div>
                                    <div style={{ marginBottom: "15px" }}>
                                        <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Complemento</label>
                                        <input
                                            type="text"
                                            name="complemento"
                                            value={formData.complemento}
                                            onChange={handleChange}
                                            style={{ width: "100%", padding: "10px", borderRadius: "4px", border: "1px solid #ddd" }}
                                        />
                                    </div>
                                </div>

                                <div style={{ marginBottom: "15px" }}>
                                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Ponto de Referência</label>
                                    <input
                                        type="text"
                                        name="ponto_referencia"
                                        value={formData.ponto_referencia}
                                        onChange={handleChange}
                                        style={{ width: "100%", padding: "10px", borderRadius: "4px", border: "1px solid #ddd" }}
                                    />
                                </div>
                                <button type="submit" style={{ padding: "10px 20px", backgroundColor: "var(--primary-color)", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}>Salvar Alterações</button>
                            </form>
                        </div>
                    )}

                    {activeTab === "seguranca" && (
                        <div>
                            <h2 style={{ marginBottom: "20px", borderBottom: "1px solid var(--border-color)", paddingBottom: "10px" }}>Segurança</h2>

                            {passwordStatus.msg && (
                                <div style={{
                                    padding: "10px",
                                    marginBottom: "15px",
                                    borderRadius: "4px",
                                    backgroundColor: passwordStatus.type === "success" ? "#e8f5e9" : passwordStatus.type === "error" ? "#ffebee" : "#e3f2fd",
                                    color: passwordStatus.type === "success" ? "#2e7d32" : passwordStatus.type === "error" ? "#c62828" : "#1565c0"
                                }}>
                                    {passwordStatus.msg}
                                </div>
                            )}

                            <form onSubmit={handlePasswordChange} style={{ maxWidth: "500px" }}>
                                <div style={{ marginBottom: "15px" }}>
                                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Senha Atual</label>
                                    <input
                                        type="password"
                                        name="currentPassword"
                                        value={passwordData.currentPassword}
                                        onChange={handlePasswordChangeInput}
                                        required
                                        style={{ width: "100%", padding: "10px", borderRadius: "4px", border: "1px solid #ddd" }}
                                    />
                                    <small style={{ color: "#666" }}>Necessário para confirmar sua identidade.</small>
                                </div>
                                <div style={{ marginBottom: "15px" }}>
                                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Nova Senha</label>
                                    <input
                                        type="password"
                                        name="newPassword"
                                        value={passwordData.newPassword}
                                        onChange={handlePasswordChangeInput}
                                        required
                                        minLength="6"
                                        style={{ width: "100%", padding: "10px", borderRadius: "4px", border: "1px solid #ddd" }}
                                    />
                                </div>
                                <div style={{ marginBottom: "15px" }}>
                                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Confirmar Nova Senha</label>
                                    <input
                                        type="password"
                                        name="confirmPassword"
                                        value={passwordData.confirmPassword}
                                        onChange={handlePasswordChangeInput}
                                        required
                                        minLength="6"
                                        style={{ width: "100%", padding: "10px", borderRadius: "4px", border: "1px solid #ddd" }}
                                    />
                                </div>
                                <button type="submit" style={{ padding: "10px 20px", backgroundColor: "var(--primary-color)", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}>Alterar Senha</button>
                            </form>
                        </div>
                    )}

                    {activeTab === "pedidos" && (
                        <div>
                            <h2 style={{ marginBottom: "20px", borderBottom: "1px solid var(--border-color)", paddingBottom: "10px" }}>Meus Pedidos</h2>
                            <div style={{ textAlign: "center", padding: "40px", color: "var(--text-secondary)" }}>
                                <i className="fas fa-box-open" style={{ fontSize: "48px", marginBottom: "20px", color: "#ddd" }}></i>
                                <p>Você ainda não realizou nenhum pedido.</p>
                                <Link to="/" style={{ color: "var(--primary-color)", fontWeight: "bold", marginTop: "10px", display: "inline-block" }}>Começar a comprar</Link>
                            </div>
                        </div>
                    )}

                    {activeTab === "favoritos" && (
                        <div>
                            <h2 style={{ marginBottom: "20px", borderBottom: "1px solid var(--border-color)", paddingBottom: "10px" }}>Meus Favoritos</h2>
                            {favoritesList.length > 0 ? (
                                <div className="grid grid-cols-1 grid-sm-2 grid-lg-3">
                                    {favoritesList.map((oferta) => (
                                        <OfertaCard
                                            key={oferta.id}
                                            oferta={oferta}
                                            user={user}
                                            isFavorite={true}
                                            onFavoriteToggle={toggleFavorite}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div style={{ textAlign: "center", padding: "40px", color: "var(--text-secondary)" }}>
                                    <i className="far fa-heart" style={{ fontSize: "48px", marginBottom: "20px", color: "#ddd" }}></i>
                                    <p>Sua lista de favoritos está vazia.</p>
                                    <Link to="/" style={{ color: "var(--primary-color)", fontWeight: "bold", marginTop: "10px", display: "inline-block" }}>Explorar ofertas</Link>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === "privacidade" && (
                        <PrivacidadeDados user={user} getFreshToken={getFreshToken} logout={logout} />
                    )}

                </div>
            </div>
        </div>
    );
};

export default UserProfile;
